import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Package, 
  MapPin, 
  CreditCard, 
  User, 
  Plus, 
  Trash2, 
  Edit2, 
  X, 
  Save, 
  Loader2 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  useGetOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useUpdateOrderItemsMutation,
  useGetProductsQuery,
  useGetPublicPricingSettingsQuery,
} from "@/store";
import { toast } from "sonner";
import { useState, useEffect, useMemo } from "react";
import type { OrderStatus } from "@/types/order";

export function OrderDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: orderData, isLoading } = useGetOrderByIdQuery(id || "");
  const [updateOrderStatus, { isLoading: isUpdating }] = useUpdateOrderStatusMutation();
  const [updateOrderItems, { isLoading: isUpdatingItems }] = useUpdateOrderItemsMutation();
  const { data: productsData } = useGetProductsQuery({ limit: 100 });
  const { data: pricingData } = useGetPublicPricingSettingsQuery();

  const [isEditing, setIsEditing] = useState(false);
  const [editedItems, setEditedItems] = useState<any[]>([]);
  const [isAddItemDialogOpen, setIsAddItemDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");

  const order = orderData?.data;
  const products = productsData?.data || [];
  const taxRate = pricingData?.data?.taxRate || 0;
  const deliveryFee = pricingData?.data?.deliveryFee || 0;

  useEffect(() => {
    if (order && !isEditing) {
      setEditedItems(order.items.map(item => ({
        id: item.id,
        productId: item.productId,
        product: item.product,
        quantity: item.quantity,
        price: item.price,
        subtotal: item.subtotal,
        variations: item.variations || {},
      })));
    }
  }, [order, isEditing]);

  // Live preview calculation
  const previewTotals = useMemo(() => {
    const subtotal = editedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const tax = subtotal * taxRate;
    
    // Apply existing coupon discount if possible
    let discount = 0;
    let actualDeliveryFee = deliveryFee;

    if (order?.couponCode && order.coupon) {
      const coupon = order.coupon;
      if (coupon.minPurchase && subtotal >= Number(coupon.minPurchase)) {
        if (coupon.discountType === "PERCENTAGE") {
          discount = subtotal * (Number(coupon.discountValue) / 100);
          if (coupon.maxDiscount) {
            discount = Math.min(discount, Number(coupon.maxDiscount));
          }
        } else {
          discount = Number(coupon.discountValue);
        }

        // Handle free delivery
        if (coupon.isFreeDelivery) {
          actualDeliveryFee = 0;
        }
      }
    }

    return {
      subtotal,
      tax,
      deliveryFee: actualDeliveryFee,
      discount,
      total: subtotal + tax + actualDeliveryFee - discount
    };
  }, [editedItems, taxRate, deliveryFee, order]);

  const formatPrice = (price: number) => {
    return `₦${price.toLocaleString()}`;
  };

  const getStatusBadgeVariant = (status: OrderStatus) => {
    switch (status) {
      case "PENDING":
        return "outline";
      case "CONFIRMED":
        return "default";
      case "PREPARING":
        return "default";
      case "READY":
        return "default";
      case "OUT_FOR_DELIVERY":
        return "default";
      case "DELIVERED":
        return "secondary";
      case "CANCELLED":
        return "destructive";
      default:
        return "outline";
    }
  };

  const handleStatusUpdate = async (newStatus: OrderStatus) => {
    if (!id || !order) return;

    try {
      await updateOrderStatus({ id, status: newStatus }).unwrap();
      toast.success("Order status updated successfully");
    } catch (error: any) {
      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        "Failed to update order status";
      toast.error(errorMessage);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleQuantityChange = (index: number, newQuantity: number) => {
    const newItems = [...editedItems];
    newItems[index] = {
      ...newItems[index],
      quantity: Math.max(1, newQuantity)
    };
    setEditedItems(newItems);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = editedItems.filter((_, i) => i !== index);
    setEditedItems(newItems);
  };

  const handleAddProduct = () => {
    const product = products.find(p => p.id === selectedProductId);
    if (!product) return;

    const newItem = {
      productId: product.id,
      product: product,
      quantity: 1,
      price: Number(product.price),
      subtotal: Number(product.price),
      variations: {}, // Variations skip for now, admin can adjust price or we can add variation selection later
    };

    setEditedItems([...editedItems, newItem]);
    setSelectedProductId("");
    setIsAddItemDialogOpen(false);
  };

  const handleSaveItems = async () => {
    if (!id) return;
    if (editedItems.length === 0) {
      toast.error("Order must have at least one item");
      return;
    }

    try {
      const itemsToSave = editedItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        variations: item.variations,
      }));

      await updateOrderItems({ id, items: itemsToSave }).unwrap();
      toast.success("Order items updated successfully");
      setIsEditing(false);
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || "Failed to update order items";
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground mb-4">Order not found</p>
        <Button onClick={() => navigate("/admin/orders")}>
          Back to Orders
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/orders")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Order #{order.displayOrderId}
            </h1>
            <p className="text-muted-foreground">
              Created on {formatDate(order.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusBadgeVariant(order.status)}>
            {order.status.replace(/_/g, " ")}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Main Content */}
        <div className="md:col-span-2 space-y-6">
          {/* Order Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Order Items</CardTitle>
                <CardDescription>
                  {isEditing ? editedItems.length : order.items.length} { (isEditing ? editedItems.length : order.items.length) === 1 ? "item" : "items"} in this order
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {!isEditing ? (
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit Items
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveItems} disabled={isUpdatingItems}>
                      {isUpdatingItems ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(isEditing ? editedItems : order.items).map((item, index) => (
                  <div
                    key={item.id || index}
                    className="flex items-start gap-4 pb-4 border-b last:border-0"
                  >
                    {item.product?.image ? (
                      <div className="h-16 w-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="h-16 w-16 rounded-md bg-muted flex items-center justify-center flex-shrink-0 text-muted-foreground">
                        <Package className="h-8 w-8" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{item.product?.name || "Product"}</p>
                      {item.variations && Object.keys(item.variations).length > 0 && (
                        <p className="text-xs text-muted-foreground mb-1">
                          {Object.entries(item.variations).map(([key, val]) => `${key}: ${val}`).join(', ')}
                        </p>
                      )}
                      {!isEditing ? (
                        <p className="text-sm text-muted-foreground">
                          Quantity: {item.quantity} × {formatPrice(item.price)}
                        </p>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-muted-foreground">Qty:</span>
                          <Input
                            type="number"
                            min="1"
                            className="h-8 w-20"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 1)}
                          />
                          <span className="text-sm text-muted-foreground ml-2">
                            × {formatPrice(item.price)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <p className="font-medium">
                        {formatPrice(isEditing ? (item.price * item.quantity) : item.subtotal)}
                      </p>
                      {isEditing && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

                {isEditing && (
                  <Button 
                    variant="outline" 
                    className="w-full border-dashed" 
                    onClick={() => setIsAddItemDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Product
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Delivery Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="font-medium">{order.deliveryAddress.label}</p>
                <p className="text-sm text-muted-foreground">
                  {order.deliveryAddress.street}
                </p>
                <p className="text-sm text-muted-foreground">
                  {order.deliveryAddress.city}, {order.deliveryAddress.state}{" "}
                  {order.deliveryAddress.zipCode}
                </p>
                {order.deliveryAddress.country && (
                  <p className="text-sm text-muted-foreground">
                    {order.deliveryAddress.country}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {order.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Order Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{order.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatPrice(isEditing ? previewTotals.subtotal : order.subtotal)}</span>
              </div>
              {(isEditing ? previewTotals.discount > 0 : (order.discount && order.discount > 0)) && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount</span>
                  <span className="text-green-600">-{formatPrice(isEditing ? previewTotals.discount : order.discount!)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span>{formatPrice(isEditing ? previewTotals.tax : order.tax)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Delivery Fee</span>
                <span>{formatPrice(isEditing ? previewTotals.deliveryFee : order.deliveryFee)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span>{formatPrice(isEditing ? previewTotals.total : order.total)}</span>
              </div>
              {isEditing && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md text-xs text-yellow-800 dark:text-yellow-400">
                  Note: Totals are estimates. Final price will be calculated on server.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{order.user?.name || "Guest"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{order.user?.email || "N/A"}</p>
              </div>
              {order.user?.phone && (
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{order.user.phone}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge
                  variant={
                    order.paymentStatus === "PAID"
                      ? "secondary"
                      : order.paymentStatus === "FAILED"
                      ? "destructive"
                      : "outline"
                  }
                >
                  {order.paymentStatus}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Method</p>
                <p className="font-medium">{order.paymentMethod}</p>
              </div>
              {order.paymentReference && (
                <div>
                  <p className="text-sm text-muted-foreground">Reference</p>
                  <p className="font-medium text-xs">{order.paymentReference}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Update Status */}
          <Card>
            <CardHeader>
              <CardTitle>Update Status</CardTitle>
              <CardDescription>Change the order status</CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={order.status}
                onValueChange={(value) => handleStatusUpdate(value as OrderStatus)}
                disabled={isUpdating}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                  <SelectItem value="PREPARING">Preparing</SelectItem>
                  <SelectItem value="READY">Ready</SelectItem>
                  <SelectItem value="OUT_FOR_DELIVERY">Out for Delivery</SelectItem>
                  <SelectItem value="DELIVERED">Delivered</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Item Dialog */}
      <Dialog open={isAddItemDialogOpen} onOpenChange={setIsAddItemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Product to Order</DialogTitle>
            <DialogDescription>
              Select a product to add to the current order.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product</Label>
              <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} - {formatPrice(Number(p.price))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddItemDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddProduct} disabled={!selectedProductId}>
              Add to Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
